import React, { type ReactNode } from "react";

import Image, { type StaticImageData } from "next/image";

import ChatIcon from "public/images/icons/support/icon-chat.svg";
import HoursIcon from "public/images/icons/support/icon-clock.svg";
import EmailIcon from "public/images/icons/support/icon-email.svg";

import Button from "components/button";
import DiscoverEnerfloButtonWithOverlay from "components/discover-enerflo-button-with-overlay";
import Heading from "components/heading";
import HeroSection from "components/hero-section";
import { loginUrl } from "components/layout/navigation";

type SupportBlockProps = { icon?: StaticImageData; title: string; children: ReactNode };

const SupportBlock = ({ icon, title, children }: SupportBlockProps) => (
  <div className="bg-white rounded-xl flex flex-1 flex-col p-4 lg:p-6">
    {icon && (
      <figure className="mb-2 text-primary h-8 w-8 md:h-10 md:w-10 mx-auto sm:mx-0">
        <Image src={icon} alt={`${title} Icon`} />
      </figure>
    )}
    <Heading tag="h3" size="h6" className="mb-2">
      {title}
    </Heading>
    <div className="grow flex flex-col items-center sm:items-start justify-between">{children}</div>
  </div>
);

const title = "Get Help & Support";

const GetHelpAndSupport = () => {
  return (
    <div className="bg-gray-100">
      <HeroSection title={title}>
        <HeroSection.Content className="space-y-16">
          <Heading tag="h2" className="mb-6 text-center">
            {title}
          </Heading>

          <div className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 text-navy sm:text-left">
              <SupportBlock icon={ChatIcon} title="Chat with Support">
                <p>
                  The fastest path to an answer. Flo responds instantly; a human specialist steps in for anything
                  complex.
                </p>
                <br />
                <Button href={loginUrl} variant="link">
                  Login to Enerflo to start a Chat
                </Button>
              </SupportBlock>
              <SupportBlock icon={EmailIcon} title="Email Support">
                <p>For detailed questions, integration setup, or anything you'd rather document in writing.</p>
                <br />
                <Button href="mailto:support@enerflo.io" variant="link" className="justify-self-end">
                  support@enerflo.io
                </Button>
              </SupportBlock>
              <SupportBlock icon={HoursIcon} title="Support Hours">
                <div>
                  <p>Monday – Saturday</p>
                  <p>6:00am – 7:00pm PT</p>
                  <p>9:00am – 10:00pm ET</p>
                  <br />
                </div>
                <p>Closed Sundays</p>
              </SupportBlock>
            </div>

            <div className="bg-navy rounded-xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-gray-200">
              <div className="p-4 lg:p-6">
                <Heading tag="h3" size="h6" className="uppercase text-primary mb-3">
                  General Inquiries
                </Heading>
                <p>Sales & Marketing Only</p>
                <Button href="tel:562-582-7728" variant="link">
                  (562) 582-7728
                </Button>
              </div>

              <div className="p-4 lg:p-6">
                <Heading tag="h3" size="h6" className="uppercase text-primary mb-3">
                  Headquarters
                </Heading>
                <p>20422 Beach Blvd #215</p>
                <p>Huntington Beach, CA 92648</p>
              </div>

              <div className="p-4 lg:p-6">
                <Heading tag="h3" size="h6" className="uppercase text-primary mb-3">
                  Book A Discovery Call
                </Heading>
                <p>Not yet an Enerflo partner?</p>
                <DiscoverEnerfloButtonWithOverlay>
                  {({ onOpen }) => (
                    <Button onClick={onOpen} variant="link">
                      Book a Discovery Call
                    </Button>
                  )}
                </DiscoverEnerfloButtonWithOverlay>
              </div>
            </div>
          </div>

          <div>
            <p className="text-gray-700 mb-6 text-center uppercase text-lg font-semibold">Additional Resources</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-navy sm:text-left">
              <SupportBlock title="Enerflo Knowledge Base">
                <p>Articles on sales training, scheduling, roles and permissions, EPC settings, and more.</p>
                <br />
                <Button href="https://help.enerflo.io/en/" variant="link">
                  Access the Knowledge Base →
                </Button>
              </SupportBlock>
              <SupportBlock title="Learning Platform">
                <p>Comprehensive training videos for sales reps. Log in to Enerflo to access.</p>
                <br />
                <Button href={loginUrl} variant="link">
                  Log in to access →
                </Button>
              </SupportBlock>
              <SupportBlock title="API Documentation">
                <p>Developer resources for Enerflo's APIs, webhooks, and integration infrastructure.</p>
                <br />
                <Button href="https://docs.enerflo.io/docs/welcome" variant="link">
                  Visit API Docs →
                </Button>
              </SupportBlock>
            </div>
          </div>
        </HeroSection.Content>
      </HeroSection>
    </div>
  );
};

export default GetHelpAndSupport;
